/**
 * Messenger channel subscriber — reuses the existing `phoenix-socket.ts`
 * singleton (same socket as presence; no second WebSocket connection).
 *
 * Exposes a small `joinConversationChannel` primitive that:
 *
 *   1. Opens or reuses the shared Phoenix socket.
 *   2. Joins `conversation:<conversationId>`.
 *   3. Wires the 7 server-fired events to the caller's handlers.
 *   4. Returns a handle with `leave()` + `pushTyping()` so the caller
 *      controls lifecycle deterministically.
 *
 * The store wiring + per-conversation set-diffing live in W3
 * (`useMessengerSubscription`) — this module stays UI-agnostic so it
 * can be reused from non-React surfaces (e.g. a notification worker).
 */

import type { Channel } from 'phoenix'
import { getPhoenixSocket } from '../../../lib/phoenix-socket'
import type { Attachment, Message } from './messenger.wire'
import { trace } from './trace'

// Phoenix's `socket.channel(topic)` ALWAYS creates a new, unjoined Channel, and
// `Channel.push()` throws "tried to push … before joining" on an unjoined one.
// So to push from outside the subscription we must reuse the channel it already
// joined (or join a fresh one if the conversation isn't currently subscribed).
interface JoinState { topic: string; state: string }

async function conversationChannel(
  userId: string,
  conversationId: string,
  userName?: string,
): Promise<Channel> {
  const socket = await getPhoenixSocket(userId, userName)
  const topic = `conversation:${conversationId}`
  const channels = (socket as unknown as { channels?: Array<Channel & JoinState> }).channels ?? []
  const existing = channels.find(
    (c) => c.topic === topic && (c.state === 'joined' || c.state === 'joining'),
  )
  if (existing) return existing

  // No live subscription for this conversation — join one now.
  const channel = socket.channel(topic, {})
  await new Promise<void>((resolve, reject) => {
    channel
      .join()
      .receive('ok', () => resolve())
      .receive('error', (err: unknown) => {
        try { channel.leave() } catch { /* ignore */ }
        reject(new Error(`join conversation:${conversationId} failed: ${JSON.stringify(err)}`))
      })
      .receive('timeout', () => {
        try { channel.leave() } catch { /* ignore */ }
        reject(new Error(`join conversation:${conversationId} timed out`))
      })
  })
  return channel
}

export interface MessengerEventHandlers {
  /** Brand-new message landed in the conversation. */
  onMessageNew?:     (message: Message) => void
  /** A message was edited. Payload is the freshly-updated wire shape. */
  onMessageUpdated?: (message: Message) => void
  /** A message was soft-deleted. Clients render placeholder + strikethrough. */
  onMessageDeleted?: (payload: { conversationId: string; messageId: string }) => void
  /** A user reacted with the given emoji. */
  onReactionAdded?:  (payload: { messageId: string; userId: string; emoji: string }) => void
  /** A user removed a reaction. */
  onReactionRemoved?:(payload: { messageId: string; userId: string; emoji: string }) => void
  /** A user updated their read cursor. */
  onReadUpdated?:    (payload: { conversationId: string; userId: string; lastReadAt: string }) => void
  /** A user updated their delivered cursor (device received messages). */
  onDeliveredUpdated?: (payload: { conversationId: string; userId: string; lastDeliveredAt: string }) => void
  /** Another user started typing. Server fans this out at most every 3s/user. */
  onTyping?:         (payload: { conversationId: string; userId: string }) => void
  /**
   * The server removed me from this conversation (an admin's removal, or my
   * own `left` landing from another device) and is about to close this
   * channel behind it with a clean `phx_close`, which phoenix-js does not
   * auto-rejoin from (only `phx_error` schedules a rejoin), so no extra
   * handling is needed to stop a retry loop here.
   */
  onConversationRemoved?: (payload: { conversationId: string }) => void
}

export interface ConversationChannelHandle {
  /** Underlying Phoenix `Channel` — exposed for advanced callers (e.g. tests). */
  channel: Channel
  /** Leave + tear down listeners. Idempotent. */
  leave: () => void
  /**
   * Push a `typing` event to the server, which fans out to other
   * subscribers as `typing` with the caller's userId. Server enforces a
   * 1-per-3s rate limit — sender-side debouncing belongs in W11.
   */
  pushTyping: () => void
}

/**
 * Open the conversation channel and register handlers in one call.
 *
 * `userId` + `userName` only matter for the initial socket token exchange
 * (same as `joinUserPresenceChannel`). Subsequent joins reuse the cached
 * socket without round-tripping `/api/token`.
 */
export async function joinConversationChannel(
  userId: string,
  conversationId: string,
  handlers: MessengerEventHandlers,
  userName?: string,
): Promise<ConversationChannelHandle> {
  const socket = await getPhoenixSocket(userId, userName)
  const channel = socket.channel(`conversation:${conversationId}`, {})

  // Wire each handler only if supplied — keeps the channel callback table
  // small for users that only care about a subset of events.
  if (handlers.onMessageNew) {
    channel.on('message:new', (p: { message: Message }) => {
      trace('socket', `event message:new  conv=${p.message?.conversationId?.slice(0, 8)}`, p.message)
      handlers.onMessageNew!(p.message)
    })
  }
  if (handlers.onMessageUpdated) {
    channel.on('message:updated', (p: { message: Message }) => handlers.onMessageUpdated!(p.message))
  }
  if (handlers.onMessageDeleted) {
    channel.on('message:deleted', (p: { conversationId: string; messageId: string }) =>
      handlers.onMessageDeleted!(p),
    )
  }
  if (handlers.onReactionAdded) {
    channel.on('reaction:added', (p: { messageId: string; userId: string; emoji: string }) =>
      handlers.onReactionAdded!(p),
    )
  }
  if (handlers.onReactionRemoved) {
    channel.on('reaction:removed', (p: { messageId: string; userId: string; emoji: string }) =>
      handlers.onReactionRemoved!(p),
    )
  }
  if (handlers.onReadUpdated) {
    channel.on('read:updated', (p: { conversationId: string; userId: string; lastReadAt: string }) => {
      trace('socket', `event read:updated  user=${p.userId?.slice(0, 8)} @ ${p.lastReadAt}`)
      handlers.onReadUpdated!(p)
    })
  }
  if (handlers.onDeliveredUpdated) {
    channel.on('delivered:updated', (p: { conversationId: string; userId: string; lastDeliveredAt: string }) =>
      handlers.onDeliveredUpdated!(p),
    )
  }
  if (handlers.onTyping) {
    channel.on('typing', (p: { conversationId: string; userId: string }) =>
      handlers.onTyping!(p),
    )
  }
  if (handlers.onConversationRemoved) {
    channel.on('conversation:removed', (p: { conversationId?: string } = {}) =>
      handlers.onConversationRemoved!({ conversationId: p.conversationId ?? conversationId }),
    )
  }

  await new Promise<void>((resolve, reject) => {
    channel
      .join()
      .receive('ok', () => resolve())
      .receive('error', (err: unknown) => {
        // Cancel phoenix-js's auto-rejoinTimer (1/2/5/10s backoff) so a
        // forbidden / left-the-conversation channel doesn't keep hammering
        // the server until the socket drops.
        try { channel.leave() } catch {/* ignore */}
        reject(new Error(`join conversation:${conversationId} failed: ${JSON.stringify(err)}`))
      })
      .receive('timeout', () => {
        try { channel.leave() } catch {/* ignore */}
        reject(new Error(`join conversation:${conversationId} timed out`))
      })
  })

  let left = false
  return {
    channel,
    leave: () => {
      if (left) return
      left = true
      channel.leave()
    },
    pushTyping: () => {
      // Fire-and-forget — typing is ephemeral; we don't surface push errors
      // to the caller. Server-side rate limit silently drops excess.
      channel.push('typing', {})
    },
  }
}

/**
 * Push a `typing` event to a conversation without going through
 * `joinConversationChannel`.
 *
 * Used by `useTyping` (W11) — typing originates from the message-input
 * component, which doesn't own the channel handle (that lives in
 * `useMessengerSubscription`). Phoenix-js caches the `Channel` per topic,
 * so this returns the same instance the subscription joined; if the
 * channel isn't joined yet, the push buffers until it is and the
 * server-side rate limit drops excess regardless.
 *
 * Caller-side throttling (1 push / 3 s) belongs in the hook.
 */
export async function pushTypingToConversation(
  userId: string,
  conversationId: string,
  userName?: string,
): Promise<void> {
  try {
    const channel = await conversationChannel(userId, conversationId, userName)
    channel.push('typing', {})
  } catch {
    /* typing is best-effort — never surface */
  }
}

export interface ChannelSendInput {
  body?: string | null
  /** Doubles as the message id + idempotency key (server uses it verbatim). */
  clientMsgId?: string
  messageType?: string
  replyTo?: string | null
  /** Full attachment objects (from upload) — echoed live to recipients; the
   *  server links them by id to the stored message. */
  attachments?: Attachment[]
}

/**
 * Signal-driven send (centralized SDK). Pushes `message:send` to the
 * conversation channel; the signaling server fans out `message:new` to live
 * members AND forwards the message to the storage pipeline (Kafka → Postgres).
 * Resolves with the server's confirmed message.
 *
 * Reuses the shared socket + the already-joined channel (phoenix caches by
 * topic) — no extra connection. This is the default send path (see
 * `useSendMessage`).
 */
export async function sendMessageToConversation(
  userId: string,
  conversationId: string,
  input: ChannelSendInput,
  userName?: string,
): Promise<{ message: Partial<Message> & { id: string } }> {
  const channel = await conversationChannel(userId, conversationId, userName)
  trace('socket', `→ message:send  conv=${conversationId.slice(0, 8)} id=${input.clientMsgId?.slice(0, 12)}`, { body: input.body, attachments: input.attachments?.length ?? 0 })
  return new Promise((resolve, reject) => {
    channel
      .push('message:send', {
        body: input.body,
        clientMsgId: input.clientMsgId,
        messageType: input.messageType ?? 'text',
        replyTo: input.replyTo ?? null,
        attachments: input.attachments ?? [],
      })
      .receive('ok', (message: Partial<Message> & { id: string }) => {
        trace('socket', `← message:send ok  id=${message.id?.slice(0, 12)}`)
        resolve({ message })
      })
      .receive('error', (err: unknown) =>
        reject(new Error(`message:send failed: ${JSON.stringify(err)}`)),
      )
      .receive('timeout', () => reject(new Error('message:send timed out')))
  })
}

/** Signal-driven edit — push `message:edit` over the conversation channel. */
export async function editMessageOnConversation(
  userId: string,
  conversationId: string,
  messageId: string,
  body: string,
  userName?: string,
): Promise<void> {
  const channel = await conversationChannel(userId, conversationId, userName)
  return new Promise((resolve, reject) => {
    channel.push('message:edit', { messageId, body })
      .receive('ok', () => resolve())
      .receive('error', (e: unknown) => reject(new Error(`message:edit failed: ${JSON.stringify(e)}`)))
      .receive('timeout', () => reject(new Error('message:edit timed out')))
  })
}

/** Signal-driven soft-delete — push `message:delete`. */
export async function deleteMessageOnConversation(
  userId: string,
  conversationId: string,
  messageId: string,
  userName?: string,
): Promise<void> {
  const channel = await conversationChannel(userId, conversationId, userName)
  return new Promise((resolve, reject) => {
    channel.push('message:delete', { messageId })
      .receive('ok', () => resolve())
      .receive('error', (e: unknown) => reject(new Error(`message:delete failed: ${JSON.stringify(e)}`)))
      .receive('timeout', () => reject(new Error('message:delete timed out')))
  })
}

/** Signal-driven reaction add/remove — push `reaction:add` / `reaction:remove`. */
export async function reactionOnConversation(
  userId: string,
  conversationId: string,
  messageId: string,
  emoji: string,
  action: 'add' | 'remove',
  userName?: string,
): Promise<void> {
  const channel = await conversationChannel(userId, conversationId, userName)
  const event = action === 'add' ? 'reaction:add' : 'reaction:remove'
  return new Promise((resolve, reject) => {
    channel.push(event, { messageId, emoji })
      .receive('ok', () => resolve())
      .receive('error', (e: unknown) => reject(new Error(`${event} failed: ${JSON.stringify(e)}`)))
      .receive('timeout', () => reject(new Error(`${event} timed out`)))
  })
}

/** A participant's delivered/read high-water-marks (for rendering ticks). */
export interface ParticipantCursor {
  userId: string
  lastDeliveredAt: string | null
  lastReadAt: string | null
}

/** One page of history from the storage read API (via the channel proxy). */
export interface WireHistoryPage {
  items: Message[]
  nextCursor: string | null
  hasMore: boolean
  /** All participants' delivered/read cursors — seeds ticks on load. */
  cursors: ParticipantCursor[]
}

// Storage read-API item shape (reactions/callPayload arrive via other paths;
// attachments ARE hydrated here so they survive reload).
interface RawHistoryItem {
  id: string
  conversationId: string
  senderId: string | null
  type: Message['type']
  body: string | null
  replyTo: string | null
  editedAt: string | null
  deletedAt: string | null
  createdAt: string
  attachments?: Attachment[]
}

function toWireMessage(m: RawHistoryItem): Message {
  return {
    id: m.id,
    conversationId: m.conversationId,
    senderId: m.senderId,
    type: m.type,
    body: m.body,
    replyTo: m.replyTo,
    callPayload: null,
    editedAt: m.editedAt,
    deletedAt: m.deletedAt,
    createdAt: m.createdAt,
    reactions: [],
    attachments: m.attachments ?? [],
  }
}

/**
 * Signal-driven history fetch. Pushes `history:fetch` to the conversation
 * channel; the signaling server (which already ACL'd the join) proxies to the
 * storage read API and returns a cursor page. Same `{items, nextCursor,
 * hasMore}` shape as the Node REST `listMessages`, so it drops into the store's
 * `seed`/`prepend` unchanged.
 */
export async function fetchHistoryFromConversation(
  userId: string,
  conversationId: string,
  opts: { before?: string | null; limit?: number },
  userName?: string,
): Promise<WireHistoryPage> {
  const channel = await conversationChannel(userId, conversationId, userName)
  trace('socket', `→ history:fetch  conv=${conversationId.slice(0, 8)} before=${opts.before ?? 'latest'} limit=${opts.limit ?? 50}`)
  return new Promise((resolve, reject) => {
    channel
      .push('history:fetch', { before: opts.before ?? null, limit: opts.limit ?? 50 })
      .receive('ok', (res: { items: RawHistoryItem[]; nextCursor: string | null; hasMore: boolean; cursors?: ParticipantCursor[] }) => {
        trace('socket', `← history:fetch  ${res.items?.length ?? 0} messages, hasMore=${res.hasMore}`)
        resolve({
          items: res.items.map(toWireMessage),
          nextCursor: res.nextCursor,
          hasMore: res.hasMore,
          cursors: res.cursors ?? [],
        })
      })
      .receive('error', (err: unknown) =>
        reject(new Error(`history:fetch failed: ${JSON.stringify(err)}`)),
      )
      .receive('timeout', () => reject(new Error('history:fetch timed out')))
  })
}

/** Delta-sync result for a conversation (local-first cache). */
export interface WireSyncResult {
  items: Message[]
  cursors: ParticipantCursor[]
  /** Server watermark — store it and pass back as `since` next time. */
  syncedAt: string
}

/**
 * Delta sync a conversation: everything changed since `since` (new / edited /
 * deleted messages + cursors). Powers the local-first IndexedDB cache — on boot
 * the client hydrates from cache, then calls this per conversation to catch up.
 */
export async function syncConversation(
  userId: string,
  conversationId: string,
  since: string | null,
  userName?: string,
): Promise<WireSyncResult> {
  const channel = await conversationChannel(userId, conversationId, userName)
  return new Promise((resolve, reject) => {
    channel
      .push('sync:fetch', { since })
      .receive('ok', (res: { items: RawHistoryItem[]; cursors?: ParticipantCursor[]; syncedAt: string }) =>
        resolve({
          items: res.items.map(toWireMessage),
          cursors: res.cursors ?? [],
          syncedAt: res.syncedAt,
        }),
      )
      .receive('error', (err: unknown) => reject(new Error(`sync:fetch failed: ${JSON.stringify(err)}`)))
      .receive('timeout', () => reject(new Error('sync:fetch timed out')))
  })
}

/**
 * Push a read-cursor update (`read:update`) over the conversation channel —
 * "I've read up to `atIso`". Server fans out `read:updated` (sender ✓✓ blue)
 * and persists the cursor. Fire-and-forget.
 */
export async function readOnConversation(
  userId: string,
  conversationId: string,
  atIso: string,
  userName?: string,
): Promise<void> {
  const channel = await conversationChannel(userId, conversationId, userName)
  channel.push('read:update', { at: atIso })
}

/**
 * Push a delivered-cursor update (`delivered:update`) — "my device received up
 * to `atIso`". Server fans out `delivered:updated` (sender ✓✓ grey) + persists.
 */
export async function deliveredOnConversation(
  userId: string,
  conversationId: string,
  atIso: string,
  userName?: string,
): Promise<void> {
  const channel = await conversationChannel(userId, conversationId, userName)
  channel.push('delivered:update', { at: atIso })
}

// Per-conversation delivered high-water-mark, so a burst of incoming messages
// collapses to at most one `delivered:update` per new-max timestamp (ISO-8601
// UTC strings compare lexically = chronologically).
const deliveredHW = new Map<string, string>()

/** Advance the delivered cursor for a conversation, deduped by high-water-mark. */
export function markDeliveredUpTo(
  userId: string,
  conversationId: string,
  atIso: string,
  userName?: string,
): void {
  if (!atIso) return
  const prev = deliveredHW.get(conversationId)
  if (prev && prev >= atIso) return
  deliveredHW.set(conversationId, atIso)
  void deliveredOnConversation(userId, conversationId, atIso, userName).catch(() => { /* non-critical */ })
}
